import { useCallback, useEffect, useState } from "react";
import { useVaultWarning } from "@/context/VaultWarningContext";

const STORAGE_KEY = "exechangar:pulseSettings";

const DEFAULTS = {
  speed: 1.0,
  intensity: 0.5,
};

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw);
    return {
      speed:
        typeof parsed.speed === "number" && parsed.speed >= 0.1 && parsed.speed <= 5.0
          ? parsed.speed
          : DEFAULTS.speed,
      intensity:
        typeof parsed.intensity === "number" && parsed.intensity >= 0.1 && parsed.intensity <= 1.0
          ? parsed.intensity
          : DEFAULTS.intensity,
    };
  } catch {
    return DEFAULTS;
  }
}

function saveToStorage(settings) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // quota exceeded or private browsing — silently ignore
  }
}

export { DEFAULTS as PULSE_DEFAULTS };

export function usePulseSettings() {
  const [settings, setSettings] = useState(loadFromStorage);

  const { setPulseSpeed: setContextSpeed, setPulseIntensity: setContextIntensity } =
    useVaultWarning();

  // Persist to localStorage and sync CSS variables + context whenever settings change
  useEffect(() => {
    saveToStorage(settings);
    document.documentElement.style.setProperty("--vault-pulse-speed", `${settings.speed}s`);
    document.documentElement.style.setProperty(
      "--vault-pulse-intensity",
      String(settings.intensity)
    );
    setContextSpeed(settings.speed);
    setContextIntensity(settings.intensity);
  }, [settings, setContextSpeed, setContextIntensity]);

  const setPulseSpeed = useCallback((speed) => {
    setSettings((prev) => ({ ...prev, speed }));
  }, []);

  const setPulseIntensity = useCallback((intensity) => {
    setSettings((prev) => ({ ...prev, intensity }));
  }, []);

  return {
    pulseSpeed: settings.speed,
    pulseIntensity: settings.intensity,
    setPulseSpeed,
    setPulseIntensity,
  };
}
